import { useEffect, useRef } from 'react';

import { useUpsertMyProfileMutation } from '@/services/socialApi';
import { useTelegram } from '@/hooks/useTelegram';
import { useAppSelector } from '@/store/hooks';
import type { UpsertMyProfileInput } from '@/types/social';

/**
 * Derive the public display name from a Telegram user object:
 *   `first_name (+ ' ' + last_name)` if a first name exists,
 *   otherwise the `username`. Returns null when neither is present.
 */
function deriveDisplayName(user: {
  first_name?: string;
  last_name?: string;
  username?: string;
}): string | null {
  const first = user.first_name?.trim();
  const last = user.last_name?.trim();
  if (first) return last ? `${first} ${last}` : first;
  const username = user.username?.trim();
  return username || null;
}

/**
 * Syncs the authenticated user's QSocials profile (displayName + avatar) from
 * the Telegram launch user, once per mount/session.
 *
 * QSocials stores friend/follow rows keyed by Logto `sub`, but Wave 2 adds a
 * `userProfiles` collection so lists can show a human name + avatar instead of
 * a raw sub. The Mini App is the only surface that knows the user's Telegram
 * `first_name` / `photo_url`, so it pushes them to qsocials via
 * `PUT /social/me/profile` on launch. Idempotent server-side — re-running with
 * the same payload is a no-op write.
 *
 * Mount once at the AppShell layer (after auth), alongside
 * usePresenceHeartbeat. No-op until `bridgeAuthenticated` is true and a
 * Telegram user with a derivable name is present (e.g. plain-web/dev runs
 * without Telegram init data simply skip the sync).
 */
export function useProfileSync(): void {
  const isAuthenticated = useAppSelector((s) => s.auth.bridgeAuthenticated);
  // QLASH user from `/users/me` — its `id` is the Laravel numeric users.id we
  // self-publish as `externalUserId` so the participants list can resolve us
  // deterministically by Laravel id (immune to Apple Hide-My-Email / social
  // login email mismatches). May be undefined until /users/me resolves.
  const qlashUserId = useAppSelector((s) => s.auth.user?.id);
  const { user } = useTelegram();
  const [upsertProfile] = useUpsertMyProfileMutation();

  // Guard so we sync at most once per mount even if deps re-run (the Telegram
  // launch user is stable, but RTK's mutation trigger identity is not).
  const syncedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !user || syncedRef.current) return;

    const displayName = deriveDisplayName(user);
    if (!displayName) return; // nothing meaningful to publish

    const payload: UpsertMyProfileInput = { displayName };
    if (user.photo_url) payload.avatarUrl = user.photo_url;
    // Only publish externalUserId once we actually know our Laravel id.
    // Omitting it leaves the server-stored value untouched (per contract).
    if (qlashUserId != null) payload.externalUserId = String(qlashUserId);

    syncedRef.current = true;
    // Fire-and-forget — a failed profile sync must never block the UI.
    upsertProfile(payload)
      .unwrap()
      .catch((err) => {
        // Allow a retry on the next auth/user change if the write failed.
        syncedRef.current = false;
        if (import.meta.env.DEV) {
          console.debug('[profile-sync] upsert failed:', err);
        }
      });
  }, [isAuthenticated, user, qlashUserId, upsertProfile]);
}
